import tailwindColors from 'tailwindcss/colors';

export interface ThemeColors {
  gridLines: number;
  cellFontColor: number;
  cellColorUserText: number;
  cellColorUserPython: number;
  cellColorUserPythonRgba: string;
  cellColorUserFormula: number;
  cellColorUserJavascript: number;
  cellColorUserAI: number;
  cellColorError: number;
  cellColorWarning: number;
  cellColorInfo: number;
  cellColorComment: number;
  cursorCell: number;
  searchCell: number;
  movingCells: number;
  gridBackground: number;
  gridBackgroundOutOfBounds: number;
  independence: number;
  headerBackgroundColor: number;
  headerSelectedBackgroundColor: number;
  headerSelectedBackgroundColorAlpha: number;
  headerSelectedRowColumnBackgroundColor: number;
  headerSelectedRowColumnBackgroundColorAlpha: number;
  headerCornerBackgroundColor: number;
  boxCellsDeleteColor: number;
  htmlPlaceholderThumbnailColor: number;
  htmlPlaceholderThumbnailBorderColor: number;
  boxCellsColor: number;
  boxCellsAlpha: number;
  gridHeadingLabel: number;
  defaultBorderColor: number;
  lightGray: string;
  mediumGray: string;
  darkGray: string;
  warning: string;
  quadraticPrimary: string;
  quadraticSecondary: string;
  quadraticThird: string;
  quadraticFourth: string;
  quadraticFifth: string;
  languagePython: string;
  languageFormula: string;
  languageAI: string;
  languagePostgres: string;
  languageMysql: string;
  languageMssql: string;
  languageSnowflake: string;
  languageCockroachdb: string;
  languageBigquery: string;
  languageMariadb: string;
  languageSupabase: string;
  languageNeon: string;
  languageMixpanel: string;
  languageJavascript: string;
  link: string;
  backgroundColor: number;
  error: string;
  cellHighlightColor: string[];
}

const lightColors: ThemeColors = {
  gridLines: 0x233143,
  cellFontColor: 0x000000,
  cellColorUserText: 0x8ecb89,
  cellColorUserPython: 0x3776ab,
  cellColorUserPythonRgba: 'rgba(55, 118, 171, 0.5)',
  cellColorUserFormula: 0x8c1a6a,
  cellColorUserJavascript: 0xca8a04,
  cellColorUserAI: 0x1a8c5d,
  cellColorError: 0xf25f5c,
  cellColorWarning: 0xffb74d,
  cellColorInfo: 0x4fc3f7,
  cellColorComment: 0xeab308,
  cursorCell: 0x2463eb,
  searchCell: Number(`0x${tailwindColors.yellow['200'].replace('#', '')}`),
  movingCells: 0x2463eb,
  gridBackground: 0xffffff,
  gridBackgroundOutOfBounds: 0xfdfdfd,
  independence: 0x5d576b,
  headerBackgroundColor: 0xffffff,
  headerSelectedBackgroundColor: 0x2463eb,
  headerSelectedBackgroundColorAlpha: 0.1,
  headerSelectedRowColumnBackgroundColor: 0x2463eb,
  headerSelectedRowColumnBackgroundColorAlpha: 0.25,
  headerCornerBackgroundColor: 0xffffff,
  boxCellsDeleteColor: Number(`0x${tailwindColors.red['400'].replace('#', '')}`),
  htmlPlaceholderThumbnailColor: 0xfefefe,
  htmlPlaceholderThumbnailBorderColor: 0x000000,
  boxCellsColor: 0x2463eb,
  boxCellsAlpha: 0.333,
  gridHeadingLabel: 0x233143,
  defaultBorderColor: 0x000000,
  lightGray: '#f6f8fa',
  mediumGray: '#cfd7de',
  darkGray: '#55606b',
  warning: '#C0803F',
  quadraticPrimary: '#6cd4ff',
  quadraticSecondary: '#8ecb89',
  quadraticThird: '#cb8999',
  quadraticFourth: '#ffc800',
  quadraticFifth: '#5d576b',
  languagePython: '#3776ab',
  languageFormula: '#8c1a6a',
  languageAI: '#1a8c5d',
  languagePostgres: '#336791',
  languageMysql: '#00758f',
  languageMssql: '#cfd8dc',
  languageSnowflake: '#249edc',
  languageCockroachdb: '#336791',
  languageBigquery: '#4285f4',
  languageMariadb: '#00758f',
  languageSupabase: '#249edc',
  languageNeon: '#336791',
  languageMixpanel: '#ffc800',
  languageJavascript: '#ca8a04',
  link: '#2463eb',
  backgroundColor: 0xffffff,
  error: '#f25f5c',
  cellHighlightColor: [
    tailwindColors.orange['600'],
    tailwindColors.fuchsia['700'],
    tailwindColors.cyan['700'],
    tailwindColors.green['700'],
    tailwindColors.indigo['600'],
    tailwindColors.pink['600'],
    tailwindColors.lime['800'],
  ],
};

const darkColors: ThemeColors = {
  gridLines: 0xcbd5e1,
  cellFontColor: 0xf8fafc,
  cellColorUserText: 0x8ecb89,
  cellColorUserPython: 0x5a9fd4,
  cellColorUserPythonRgba: 'rgba(90, 159, 212, 0.5)',
  cellColorUserFormula: 0xc76fa8,
  cellColorUserJavascript: 0xeab308,
  cellColorUserAI: 0x34d399,
  cellColorError: 0xf87171,
  cellColorWarning: 0xfbbf24,
  cellColorInfo: 0x38bdf8,
  cellColorComment: 0xfacc15,
  cursorCell: 0x3b82f6,
  searchCell: Number(`0x${tailwindColors.yellow['700'].replace('#', '')}`),
  movingCells: 0x3b82f6,
  gridBackground: 0x030711,
  gridBackgroundOutOfBounds: 0x020509,
  independence: 0x94a3b8,
  headerBackgroundColor: 0x030711,
  headerSelectedBackgroundColor: 0x3b82f6,
  headerSelectedBackgroundColorAlpha: 0.15,
  headerSelectedRowColumnBackgroundColor: 0x3b82f6,
  headerSelectedRowColumnBackgroundColorAlpha: 0.3,
  headerCornerBackgroundColor: 0x030711,
  boxCellsDeleteColor: Number(`0x${tailwindColors.red['500'].replace('#', '')}`),
  htmlPlaceholderThumbnailColor: 0x0f172a,
  htmlPlaceholderThumbnailBorderColor: 0xffffff,
  boxCellsColor: 0x3b82f6,
  boxCellsAlpha: 0.4,
  gridHeadingLabel: 0x94a3b8,
  defaultBorderColor: 0xffffff,
  lightGray: '#1e293b',
  mediumGray: '#475569',
  darkGray: '#cbd5e1',
  warning: '#f59e0b',
  quadraticPrimary: '#6cd4ff',
  quadraticSecondary: '#8ecb89',
  quadraticThird: '#cb8999',
  quadraticFourth: '#ffc800',
  quadraticFifth: '#94a3b8',
  languagePython: '#5a9fd4',
  languageFormula: '#c76fa8',
  languageAI: '#34d399',
  languagePostgres: '#60a5fa',
  languageMysql: '#22d3ee',
  languageMssql: '#94a3b8',
  languageSnowflake: '#38bdf8',
  languageCockroachdb: '#60a5fa',
  languageBigquery: '#60a5fa',
  languageMariadb: '#22d3ee',
  languageSupabase: '#38bdf8',
  languageNeon: '#60a5fa',
  languageMixpanel: '#fbbf24',
  languageJavascript: '#eab308',
  link: '#60a5fa',
  backgroundColor: 0x030711,
  error: '#f87171',
  cellHighlightColor: [
    tailwindColors.orange['500'],
    tailwindColors.fuchsia['500'],
    tailwindColors.cyan['500'],
    tailwindColors.green['500'],
    tailwindColors.indigo['500'],
    tailwindColors.pink['500'],
    tailwindColors.lime['500'],
  ],
};

// NOTE: This logic is duplicated in index.html to prevent flash of light mode.
// If you change the localStorage key or parsing logic here, update index.html too.
function getInitialDarkMode(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const stored = localStorage.getItem('useThemeAppearanceMode');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed === 'dark') return true;
      if (parsed === 'light') return false;
      if (parsed === 'system') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
    }
  } catch {
    // Ignore localStorage errors
  }
  return false;
}

const initialDarkMode = getInitialDarkMode();
let currentColors: ThemeColors = initialDarkMode ? darkColors : lightColors;
let isDarkMode = initialDarkMode;

export function setColorMode(dark: boolean): void {
  isDarkMode = dark;
  currentColors = dark ? darkColors : lightColors;
}

export function getColorMode(): boolean {
  return isDarkMode;
}

export function getColors(): ThemeColors {
  return currentColors;
}

export const colors = new Proxy({} as ThemeColors, {
  get(_target, prop: string | symbol) {
    if (typeof prop === 'string' && prop in currentColors) {
      return currentColors[prop as keyof ThemeColors];
    }
    return undefined;
  },
});
